import React, { useState, useEffect } from 'react';
import { createBuilding, updateBuilding, getProjectBuildings, uploadBuildingCover } from '../services/api';
import './BuildingModal.css';

const BuildingModal = ({ isOpen, onClose, onSave, building, projectId }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    floors_count: 1,
    image_cover_url: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [coverFile, setCoverFile] = useState(null);

  const handleCoverFolderSelect = async (e) => {
    try {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      // Filter to image files only
      const imageFiles = files.filter(f => {
        if (f.type && f.type.startsWith('image/')) return true;
        const name = (f.name || '').toLowerCase();
        return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'].some(ext => name.endsWith(ext));
      });
      if (!imageFiles.length) return;
      // Prefer earliest by relative path if available, else by name
      const sorted = imageFiles.sort((a, b) => {
        const ap = a.webkitRelativePath || a.name || '';
        const bp = b.webkitRelativePath || b.name || '';
        return ap.localeCompare(bp);
      });
      setCoverFile(sorted[0]);
    } catch (err) {
      console.error('Error selecting folder for cover:', err);
    } finally {
      // Reset input so the same folder can be reselected if needed
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (building) {
      setFormData({
        name: building.name || '',
        description: building.description || '',
        address: building.address || '',
        floors_count: building.floors_count || 1,
        image_cover_url: building.image_cover_url || '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        address: '',
        floors_count: 1,
        image_cover_url: '',
      });
    }
    setError('');
  }, [building, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 1 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let result;
      if (building) {
        // Update existing building
        result = await updateBuilding(building.id, formData);
      } else {
        // If creating new: check existing buildings by name within the project to avoid duplicates
        const existing = await getProjectBuildings(projectId).catch(() => []);
        const match = existing.find(b => (b.name || '').trim().toLowerCase() === (formData.name || '').trim().toLowerCase());
        if (match) {
          // Reuse existing
          result = match;
        } else {
          // Create new building
          result = await createBuilding({
            ...formData,
            project_id: projectId
          });
        }
      }

      // Upload selected cover image to uploads/cover/<buildingId> and update image_cover_url

      // If a cover image is selected, upload it to uploads/cover/<buildingId> and update image_cover_url
      if (coverFile) {
        try {
          const targetBuildingId = building ? building.id : result.id;
          const coverResp = await uploadBuildingCover(targetBuildingId, coverFile);
          if (coverResp && coverResp.image_cover_url) {
            result = { ...result, image_cover_url: coverResp.image_cover_url };
          }
        } catch (coverErr) {
          console.error('Error uploading cover image:', coverErr);
          // Continue; building is saved even if cover upload fails
        }
      }
      
      onSave(result);
      onClose();
    } catch (error) {
      console.error('Error saving building:', error);
      setError(error.message || 'Failed to save building');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content building-modal">
        <div className="modal-header">
          <h2>{building ? 'Edit Building' : 'Add New Building'}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="building-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="name">Building Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Enter building name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter building description"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label htmlFor="address">Address</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter building address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="floors_count">Number of Floors</label>
            <input
              type="number"
              id="floors_count"
              name="floors_count"
              value={formData.floors_count}
              onChange={handleInputChange}
              min="1"
              max="200"
            />
          </div>

          

          <div className="form-group">
            <label htmlFor="cover_upload">Upload Cover Image (stores in uploads/cover/buildingId)</label>
            <input
              type="file"
              id="cover_upload"
              name="cover_upload"
              accept="image/*"
              onChange={(e) => setCoverFile((e.target.files && e.target.files[0]) || null)}
            />
            {coverFile && (
              <div className="helper-text">Selected cover: {coverFile.name}</div>
            )}
            {formData.image_cover_url && (
              <div className="helper-text">Current cover URL: {formData.image_cover_url}</div>
            )}
            <div className="helper-text" style={{ marginTop: '8px' }}>
              Or select a folder; the first image will be used as cover.
            </div>
            <label className="button-as-label" style={{ display: 'inline-block', marginTop: '8px' }}>
              <span className="browse-button">Upload Folder</span>
              <input
                type="file"
                webkitdirectory="true"
                multiple
                style={{ display: 'none' }}
                onChange={handleCoverFolderSelect}
              />
            </label>
          </div>

          <div className="form-group">
            <div className="helper-text" style={{ marginTop: '4px' }}>
              For uploading building images by elevation and floor, use the Elevation menu.
            </div>
            <a
              href={`?view=elevations&projectId=${projectId || ''}${formData?.id ? `&buildingId=${formData.id}` : ''}`}
              className="browse-button"
              style={{ display: 'inline-block', marginTop: 8 }}
            >
              Go to Elevations Upload
            </a>
          </div>

          

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="save-button">
              {loading ? 'Saving...' : (building ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BuildingModal;